import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Percent, ShieldCheck, AlertTriangle, CheckCircle2, Landmark, FileCheck2, Zap } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";

const TABS = [
    { key: "3b", label: "GSTR-3B · Summary", icon: Landmark },
    { key: "1", label: "GSTR-1 · Outward", icon: FileCheck2 },
    { key: "2b", label: "GSTR-2B · Inward Recon", icon: ShieldCheck },
    { key: "itc", label: "ITC Tracker", icon: Percent },
    { key: "einv", label: "E-Invoice IRN", icon: Zap },
];

export default function GSTDashboard() {
    const [tab, setTab] = useState("3b");
    const [period, setPeriod] = useState("");
    const [gstr1, setGstr1] = useState(null);
    const [gstr3b, setGstr3b] = useState(null);
    const [gstr2b, setGstr2b] = useState(null);
    const [itc, setItc] = useState(null);
    const [salesVouchers, setSalesVouchers] = useState([]);

    const refresh = async () => {
        const [g1, g3b, g2b, itcRes, sv] = await Promise.all([
            api.get(`/gst/gstr1${period ? `?period=${period}` : ""}`),
            api.get(`/gst/gstr3b${period ? `?period=${period}` : ""}`),
            api.get(`/gst/gstr2b${period ? `?period=${period}` : ""}`),
            api.get("/gst/itc-tracker"),
            api.get("/entry/vouchers?voucher_type=SALES"),
        ]);
        setGstr1(g1.data); setGstr3b(g3b.data); setGstr2b(g2b.data); setItc(itcRes.data);
        setSalesVouchers(sv.data.vouchers);
    };
    useEffect(() => { refresh(); }, [period]);

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// GST DASHBOARD · INDIA</div>
                    <h1 className="font-display font-black text-3xl flex items-center gap-3"><Percent className="text-[var(--brand-amber)]" size={26} /> GSTR-1 · 3B · 2B + ITC & E-Invoice</h1>
                    <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                        Auto-derived from live SALES + PURCHASE vouchers · Filing-ready
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Period:</label>
                    <input data-testid="gst-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs font-mono-fin text-[var(--text-primary)]" placeholder="All" />
                    {period && <button onClick={() => setPeriod("")} className="text-[10px] text-[var(--brand-amber)] uppercase">Clear</button>}
                </div>
            </div>

            {gstr3b && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KpiCard testId="gst-outward" label="Outward Tax" value={gstr3b.outward.total_tax} sub={`Taxable ${fmtINR(gstr3b.outward.taxable_value)}`} accent />
                    <KpiCard testId="gst-itc" label="ITC Available" value={gstr3b.itc.total_available} sub={`Taxable ${fmtINR(gstr3b.itc.taxable_value)}`} />
                    <KpiCard testId="gst-net" label="Net Payable" value={gstr3b.net_liability.total_payable} sub="After ITC set-off" />
                    <KpiCard testId="gst-cf" label="ITC Carry-Fwd" value={gstr3b.carry_forward} sub="Unused ITC" />
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button key={t.key} data-testid={`gst-tab-${t.key}`} onClick={() => setTab(t.key)} className={`px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${tab === t.key ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)] text-[var(--brand-amber)]" : "border-panel text-[var(--text-secondary)] hover:border-[var(--brand-amber)]"}`}>
                            <Icon size={12} /> <span className="text-[10px] font-mono-fin uppercase tracking-wider">{t.label}</span>
                        </button>
                    );
                })}
            </div>

            {tab === "3b" && gstr3b && <GSTR3B data={gstr3b} />}
            {tab === "1" && gstr1 && <GSTR1 data={gstr1} />}
            {tab === "2b" && gstr2b && <GSTR2B data={gstr2b} />}
            {tab === "itc" && itc && <ITCTracker data={itc} />}
            {tab === "einv" && <EInvoicePanel salesVouchers={salesVouchers} onRefresh={refresh} />}
        </div>
    );
}

function GSTR3B({ data }) {
    const rows = [
        { label: "3.1 Outward Taxable Supplies", val: data.outward.taxable_value, tax: data.outward.total_tax, cgst: data.outward.cgst, sgst: data.outward.sgst, igst: data.outward.igst },
        { label: "4 ITC Available", val: data.itc.taxable_value, tax: data.itc.total_available, cgst: data.itc.cgst, sgst: data.itc.sgst, igst: data.itc.igst },
        { label: "6.1 Net Payable (Cash Ledger)", val: 0, tax: data.net_liability.total_payable, cgst: data.net_liability.cgst, sgst: data.net_liability.sgst, igst: data.net_liability.igst, accent: true },
    ];
    return (
        <Panel testId="gst-3b-panel" title="GSTR-3B · Summary Return" subtitle={`Period: ${data.period}`}>
            <table className="w-full font-mono-fin text-xs">
                <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                    <th className="text-left py-2">Line</th><th className="text-right">Taxable Value</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th><th className="text-right">Total Tax</th>
                </tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className={`border-b border-[var(--border)]/50 ${r.accent ? "bg-[var(--brand-amber-dim)]" : ""}`}>
                            <td className="py-2 text-[var(--text-primary)]">{r.label}</td>
                            <td className="text-right text-[var(--text-secondary)]">{r.val ? fmtINR(r.val) : "—"}</td>
                            <td className="text-right text-[var(--text-primary)]">{fmtINR(r.cgst)}</td>
                            <td className="text-right text-[var(--text-primary)]">{fmtINR(r.sgst)}</td>
                            <td className="text-right text-[var(--text-primary)]">{fmtINR(r.igst)}</td>
                            <td className={`text-right font-semibold ${r.accent ? "text-[var(--brand-amber)]" : "text-amber"}`}>{fmtINR(r.tax)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="mt-4 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">
                Note: 50-50 CGST/SGST split for intra-state (Madhya Pradesh). Update with IGST if any inter-state supplies.
            </div>
        </Panel>
    );
}

function GSTR1({ data }) {
    return (
        <Panel testId="gst-1-panel" title="GSTR-1 · Outward Supplies" subtitle={`${data.summary.total_invoices} invoices · Taxable ${fmtINR(data.summary.total_taxable)} · Tax ${fmtINR(data.summary.total_tax)}`}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                {Object.entries(data.slab_totals).map(([slab, val]) => (
                    <div key={slab} className="border border-panel p-2 bg-[var(--bg-panel-hover)]">
                        <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">{slab} slab</div>
                        <div className="text-xs text-[var(--text-primary)] font-mono-fin mt-1">{fmtINR(val.taxable)}</div>
                        <div className="text-[10px] text-amber font-mono-fin">tax: {fmtINR(val.tax)}</div>
                    </div>
                ))}
            </div>
            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">B2C (Consumer) — {data.b2c.length}</div>
            {data.b2c.length > 0 ? (
                <div className="overflow-x-auto mb-4">
                    <table className="w-full font-mono-fin text-xs">
                        <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                            <th className="text-left py-1">Invoice</th><th>Date</th><th>Party</th><th className="text-right">Taxable</th><th className="text-right">Rate</th><th className="text-right">Tax</th><th className="text-right">Total</th>
                        </tr></thead>
                        <tbody>
                            {data.b2c.map((r, i) => (
                                <tr key={i} className="border-b border-[var(--border)]/50">
                                    <td className="py-1 text-[var(--brand-amber)]">{r.invoice_no}</td>
                                    <td className="text-[var(--text-secondary)]">{fmtDate(r.invoice_date)}</td>
                                    <td className="text-[var(--text-primary)]">{r.party_name}</td>
                                    <td className="text-right text-[var(--text-primary)]">{fmtINR(r.taxable_value)}</td>
                                    <td className="text-right">{r.gst_rate}%</td>
                                    <td className="text-right text-amber">{fmtINR(r.gst_amount)}</td>
                                    <td className="text-right text-pos font-semibold">{fmtINR(r.invoice_value)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : <div className="text-xs text-[var(--text-secondary)] py-2">No B2C invoices in this period.</div>}
        </Panel>
    );
}

function GSTR2B({ data }) {
    const colorMap = { "Matched": "text-pos", "Mismatched": "text-amber", "Not Uploaded by Vendor": "text-neg" };
    return (
        <Panel testId="gst-2b-panel" title="GSTR-2B · Inward Supplies Reconciliation" subtitle={`Match rate: ${Math.round(100 * data.summary.matched / Math.max(1, data.summary.total))}% · ITC at risk ${fmtINR(data.summary.itc_at_risk)}`}>
            <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="border border-pos/40 p-3 bg-pos/5"><div className="text-[10px] uppercase text-[var(--text-muted)]">Matched</div><div className="text-lg text-pos font-mono-fin mt-1">{data.summary.matched}</div></div>
                <div className="border border-amber/40 p-3 bg-amber/5"><div className="text-[10px] uppercase text-[var(--text-muted)]">Mismatched</div><div className="text-lg text-amber font-mono-fin mt-1">{data.summary.mismatched}</div></div>
                <div className="border border-neg/40 p-3 bg-neg/5"><div className="text-[10px] uppercase text-[var(--text-muted)]">Missing</div><div className="text-lg text-neg font-mono-fin mt-1">{data.summary.missing}</div></div>
            </div>
            {data.rows.length === 0 ? <div className="text-xs text-[var(--text-secondary)] py-4 text-center uppercase">No purchases yet. Post via Quick Entry / PURCHASE.</div> : (
                <div className="overflow-x-auto">
                    <table className="w-full font-mono-fin text-xs">
                        <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                            <th className="text-left py-1">Invoice</th><th>Date</th><th>Vendor</th><th className="text-right">Taxable</th><th className="text-right">Tax</th><th>Recon Status</th>
                        </tr></thead>
                        <tbody>
                            {data.rows.map((r, i) => (
                                <tr key={i} className="border-b border-[var(--border)]/50">
                                    <td className="py-1 text-[var(--brand-amber)]">{r.invoice_no}</td>
                                    <td className="text-[var(--text-secondary)]">{fmtDate(r.invoice_date)}</td>
                                    <td className="text-[var(--text-primary)]">{r.vendor_name}</td>
                                    <td className="text-right">{fmtINR(r.taxable_value)}</td>
                                    <td className="text-right text-amber">{fmtINR(r.gst_amount)}</td>
                                    <td className={colorMap[r.recon_status] || "text-[var(--text-primary)]"}>{r.recon_status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Panel>
    );
}

function ITCTracker({ data }) {
    return (
        <Panel testId="gst-itc-panel" title="ITC Rolling Balance" subtitle={`Current ITC balance: ${fmtINR(data.current_balance)}`}>
            {data.itc_series.length === 0 ? <div className="text-xs text-[var(--text-secondary)] py-4 text-center">No purchase/sales history yet.</div> : (
                <div className="h-64" style={{ minHeight: "16rem" }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.itc_series}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="month" stroke="#94a3b8" fontSize={10} />
                            <YAxis stroke="#94a3b8" fontSize={10} />
                            <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0" }} formatter={(v) => fmtINR(v)} />
                            <Bar dataKey="itc_added" fill="#fb8b1e" name="ITC Added" />
                            <Bar dataKey="utilised" fill="#4af6c3" name="Utilised" />
                            <Bar dataKey="closing" fill="#60a5fa" name="Closing" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
            {data.itc_series.length > 0 && (
                <table className="w-full font-mono-fin text-xs mt-3">
                    <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                        <th className="text-left py-1">Month</th><th className="text-right">Opening</th><th className="text-right">ITC Added</th><th className="text-right">Utilised</th><th className="text-right">Closing</th>
                    </tr></thead>
                    <tbody>
                        {data.itc_series.map((r, i) => (
                            <tr key={i} className="border-b border-[var(--border)]/50">
                                <td className="py-1 text-[var(--text-primary)]">{r.month}</td>
                                <td className="text-right text-[var(--text-secondary)]">{fmtINR(r.opening)}</td>
                                <td className="text-right text-amber">{fmtINR(r.itc_added)}</td>
                                <td className="text-right text-pos">{fmtINR(r.utilised)}</td>
                                <td className="text-right text-[var(--text-primary)] font-semibold">{fmtINR(r.closing)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Panel>
    );
}

function EInvoicePanel({ salesVouchers, onRefresh }) {
    const gen = async (v) => {
        try {
            const res = await api.post("/gst/e-invoice", { voucher_id: v.id });
            toast.success(`IRN generated: ${res.data.irn.slice(0, 12)}...`);
            onRefresh();
        } catch (e) { toast.error("Failed: " + (e.response?.data?.detail || e.message)); }
    };
    return (
        <Panel testId="gst-einv-panel" title="E-Invoice IRN Generator (NIC-mock)" subtitle={`${salesVouchers.length} sales vouchers · Click to generate IRN for each`}>
            {salesVouchers.length === 0 ? <div className="text-xs text-[var(--text-secondary)] py-4 text-center uppercase">No sales yet. Post via Quick Entry / SALES.</div> : (
                <div className="overflow-x-auto">
                    <table className="w-full font-mono-fin text-xs">
                        <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                            <th className="text-left py-1">Invoice</th><th>Date</th><th>Party</th><th className="text-right">Value</th><th>IRN Status</th><th></th>
                        </tr></thead>
                        <tbody>
                            {salesVouchers.map((v) => (
                                <tr key={v.id} className="border-b border-[var(--border)]/50">
                                    <td className="py-1 text-[var(--brand-amber)]">{v.invoice_no || v.voucher_no}</td>
                                    <td className="text-[var(--text-secondary)]">{fmtDate(v.date)}</td>
                                    <td className="text-[var(--text-primary)]">{v.party_name}</td>
                                    <td className="text-right text-amber">{fmtINR((v.amount || 0) + (v.gst_amount || 0))}</td>
                                    <td>{v.einvoice ? <span className="text-pos flex items-center gap-1"><CheckCircle2 size={11} /> {v.einvoice.status}</span> : <span className="text-[var(--text-muted)] flex items-center gap-1"><AlertTriangle size={11} /> Pending</span>}</td>
                                    <td>
                                        {v.einvoice ? (
                                            <div className="text-[9px] font-mono-fin text-[var(--text-muted)]">IRN {v.einvoice.irn.slice(0, 16)}...</div>
                                        ) : (
                                            <button data-testid={`gst-gen-${v.id}`} onClick={() => gen(v)} className="text-[10px] uppercase tracking-wider text-[var(--brand-amber)] hover:underline">Generate IRN</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Panel>
    );
}
